import React from 'react';
import { motion } from 'framer-motion';
import { 
  UsersIcon, 
  LightBulbIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import AnimatedBackground from './AnimatedBackground';

const About = () => {
  const features = [
    {
      icon: LightBulbIcon,
      title: 'Innovation First',
      description: 'We stay ahead of technology trends to deliver cutting-edge solutions.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality control ensure flawless deliverables.',
    },
    {
      icon: ClockIcon,
      title: 'On-Time Delivery',
      description: 'We respect deadlines and deliver projects when promised.',
    },
    {
      icon: HeartIcon,
      title: 'Client-Focused',
      description: 'Your success is our priority. We build lasting partnerships.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="about" className="py-24 bg-gray-800 relative overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Gradient Orbs */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-green-600/10 to-green-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 drop-shadow-lg">
            Leading the Future of{' '}
            <span className="bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent drop-shadow-lg">
              IT Innovation
            </span>
          </h2>
          
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            Your trusted partner in digital transformation
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-white mb-6">
              Transforming Businesses Through Technology
            </h3>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              At BrightCode, we specialize in delivering comprehensive IT solutions that drive business growth. Our team of experienced developers, SEO specialists, and IT consultants work together to provide cutting-edge services that meet the evolving needs of modern businesses.
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              We believe in the power of technology to transform businesses and create meaningful impact. Our approach combines technical expertise with creative problem-solving to deliver solutions that not only meet today's requirements but also prepare you for tomorrow's opportunities.
            </p>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-8">
              {[
                { number: '100+', label: 'Projects Completed', color: 'text-green-400' },
                { number: '50+', label: 'Happy Clients', color: 'text-green-500' },
                { number: '24/7', label: 'Support Available', color: 'text-green-300' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`text-3xl font-bold ${stat.color} mb-2`}>
                    {stat.number}
                  </div>
                  <div className="text-gray-400 font-medium text-sm">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative bg-gradient-to-br from-green-600 to-green-500 rounded-3xl p-8 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-green-700/90 to-green-600/90 rounded-3xl"></div>
              <div className="relative z-10 text-center text-white">
                <UsersIcon className="w-24 h-24 mx-auto mb-6 opacity-90" />
                <h4 className="text-2xl font-bold mb-4">Professional IT Team</h4>
                <p className="text-green-100 leading-relaxed">
                  Our diverse team of experts combines years of experience with fresh perspectives to deliver exceptional results for every project.
                </p>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-white/20 rounded-full animate-bounce-slow"></div>
              <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-white/30 rounded-full animate-pulse"></div>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <motion.div
                key={index}
                className="group text-center"
                variants={itemVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center group-hover:from-green-400 group-hover:to-green-500 transition-all duration-300 shadow-lg group-hover:shadow-xl">
                    <IconComponent className="w-10 h-10 text-white group-hover:text-white transition-colors duration-300" />
                  </div>
                  
                  <h4 className="text-xl font-bold text-white mb-3 group-hover:text-green-400 transition-colors duration-300">
                    {feature.title}
                  </h4>
                  
                  <p className="text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  {/* Hover Effect Line */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 group-hover:w-16 transition-all duration-300"></div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
